import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Upload,
  FileWarning,
  Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface ValidationResult {
  tenant_id: string;
  total_contacts: number;
  valid_contacts: number;
  invalid_contacts: number;
  validation_passed: boolean;
  invalid_details: Array<{
    index: number;
    contact: any;
    errors: string[];
  }>;
  warnings: string[];
  validated_at: string;
}

interface ContactImportValidationModalProps {
  open: boolean;
  onClose: () => void;
  validationResult: ValidationResult | null;
  onImportValidOnly: () => void;
  onFixAndReupload: () => void;
}

export const ContactImportValidationModal: React.FC<ContactImportValidationModalProps> = ({
  open,
  onClose,
  validationResult,
  onImportValidOnly,
  onFixAndReupload,
}) => {
  if (!validationResult) return null;

  const hasErrors = !validationResult.validation_passed;
  const hasWarnings = validationResult.warnings && validationResult.warnings.length > 0;
  const errorPercentage = validationResult.total_contacts > 0
    ? (validationResult.invalid_contacts / validationResult.total_contacts) * 100
    : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
              hasErrors ? 'bg-red-100' : 'bg-green-100'
            }`}>
              {hasErrors ? (
                <FileWarning className="h-6 w-6 text-red-600" />
              ) : (
                <CheckCircle className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div>
              <DialogTitle className="text-2xl">
                {hasErrors ? 'Import Validation Issues Found' : 'Validation Successful'}
              </DialogTitle>
              <DialogDescription className="text-base mt-1">
                {hasErrors
                  ? 'Some contacts have validation errors that need attention'
                  : 'All contacts passed validation checks'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Contacts</p>
            <p className="text-2xl font-bold text-gray-900">{validationResult.total_contacts}</p>
          </div>
          <div className={`${
            validationResult.valid_contacts > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          } border rounded-lg p-4`}>
            <p className="text-sm text-gray-600">Valid Contacts</p>
            <p className={`text-2xl font-bold ${
              validationResult.valid_contacts > 0 ? 'text-green-900' : 'text-gray-900'
            }`}>
              {validationResult.valid_contacts}
            </p>
          </div>
          <div className={`${
            validationResult.invalid_contacts > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
          } border rounded-lg p-4`}>
            <p className="text-sm text-gray-600">Invalid Contacts</p>
            <p className={`text-2xl font-bold ${
              validationResult.invalid_contacts > 0 ? 'text-red-900' : 'text-gray-900'
            }`}>
              {validationResult.invalid_contacts}
            </p>
          </div>
        </div>

        {/* Warnings */}
        {hasWarnings && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <ul className="list-disc list-inside space-y-1">
                {validationResult.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Error Details */}
        {hasErrors && validationResult.invalid_details.length > 0 && (
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Validation Errors (showing first 10)
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader className="bg-gray-50 sticky top-0">
                    <TableRow>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validationResult.invalid_details.map((detail, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{detail.index}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {detail.contact.email && (
                              <div className="text-sm">{detail.contact.email}</div>
                            )}
                            {(detail.contact.first_name || detail.contact.last_name) && (
                              <div className="text-xs text-gray-600">
                                {detail.contact.first_name} {detail.contact.last_name}
                              </div>
                            )}
                            {detail.contact.phone && (
                              <div className="text-xs text-gray-600">{detail.contact.phone}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {detail.errors.map((error, errorIdx) => (
                              <Badge key={errorIdx} variant="destructive" className="mr-1 mb-1">
                                {error}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* Best Practices Guide */}
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <p className="font-semibold mb-2">CSV Format Requirements:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Required columns:</strong> email, first_name, last_name</li>
              <li><strong>For SMS compliance:</strong> phone, sms_consent (true/false), consent_date (YYYY-MM-DD), consent_method (web_form/paper_form/verbal/etc.)</li>
              <li><strong>Optional:</strong> company, tags</li>
            </ul>
            <p className="mt-2 text-sm">
              <Link to="/settings" className="underline font-medium">
                Visit Compliance Settings
              </Link>
              {' '}for more information about SMS consent requirements.
            </p>
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onFixAndReupload}>
              <Upload className="h-4 w-4 mr-2" />
              Fix in CSV and Re-upload
            </Button>
            {validationResult.valid_contacts > 0 && (
              <Button onClick={onImportValidOnly}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Import {validationResult.valid_contacts} Valid Contact{validationResult.valid_contacts !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>

        {errorPercentage > 50 && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Over {errorPercentage.toFixed(0)}% of your contacts have validation errors. 
              We recommend reviewing your CSV file and ensuring all required fields are properly formatted before importing.
            </AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

